import { mergeViewerOptions } from "./viewer-options.js";

/** @typedef {import('@splidejs/splide').Options} SplideOptions */

import ExpandHandler from "./controllers/ExpandHandler.js";
import PanZoomController from "./controllers/PanZoomController.js";
import SidebarGestureController from "./controllers/SidebarGestureController.js";
import SplideHandler from "./controllers/SplideHandler.js";
import Controls from "./components/Controls.js";
import DownloadDialog from "./components/DownloadDialog.js";
import PageAssets from "./components/PageAssets.js";
import SidebarPanels from "./components/SidebarPanels.js";
import { VERBOSE } from "./config.js";

const EXPANDED_HEIGHT_SETTLE_FRAMES = 60;

export function getAvailableViewportHeight(view = window) {
  // outerHeight is integer-valued and Firefox Android rounds the actually
  // usable fullscreen height up. Keep it one pixel inside that boundary.
  const conservativeOuterHeight = Number.isFinite(view.outerHeight)
    ? Math.floor(view.outerHeight) - 1
    : undefined;
  const candidates = [
    view.visualViewport?.height,
    view.innerHeight,
    view.document?.documentElement?.clientHeight,
    conservativeOuterHeight,
  ].filter((height) => Number.isFinite(height) && height > 0);

  if (!candidates.length) return undefined;

  // During mobile fullscreen transitions one viewport API can temporarily
  // retain the old, larger value while another already reports the usable
  // area. Using the smallest valid value prevents vertical overflow.
  return Math.floor(Math.min(...candidates));
}

/*!
 * RheaBookViewer for viewing RheaFlibooks
 * Version: 1.0.0
 * License: UNLICENSED
 * Copyright Sebastian Wolfertz
 */
export default class RheaBookViewer {
  /**
   * @typedef {Object} Page
   */

  /**
   * @typedef {Object} BookInfo
   * @property {string} index
   * @property {string} path
   * @property {string} filePath
   * @property {string} name
   * @property {string} filename
   * @property {array} toc
   * @property {boolean} assetsPending
   * @property {number} assetRetryInterval
   * @property {number} assetRetryAttempts
   * @property {Page[]} pageArray
   */

  /**
   *
   * @type {{
   * class: string,
   * fullscreen: string,
   * breakpoint: number
   * toc: boolean,
   * thumbnails: boolean,
   * zoom: number,
   * maxZoom: number,
   * splideOptions: SplideOptions,
   * i18n: {
   *      next: string,
   *      play: string,
   *      select: string,
   *      prev: string,
   *      toc: string,
   *      zoomOut: string,
   *      carousel: string,
   *      downloadRightPage: string,
   *      close: string,
   *      slideLabel: string,
   *      toPage: string,
   *      last: string,
   *      compress: string,
   *      zoomIn: string,
   *      slideX: string,
   *      downloadLeftPage: string,
   *      pause: string,
   *      downloadPage: string,
   *      expand: string,
   *      slide: string,
   *      downloadAll: string,
   *      changeDisplay: string,
   *      page: string,
   *      thumbnails: string,
   *      moreControls: string,
   *      closeControls: string,
   *      first: string,
   *      pageX: string
   * }}}
   */

  // Default configuration
  static defaults = {
    class: "book",
    thumbnails: false,
    fullscreen: "modal",
    toc: true,
    breakpoint: 720,
    zoom: 4 / 3,
    touchZoom: 5 / 2,
    maxZoom: 7.5,

    splideOptions: {
      type: "slide",
      lazyLoad: "nearby",
      preloadPages: 2,
      // Splide 4.1.4 binds boolean `true` to the root element even
      // though its public option docs describe it as global. Use the
      // explicit mode so arrow keys work without focusing a control.
      keyboard: "global",
      speed: 800,
      perPage: 2,
      urlHash: true,
      arrows: true,
      pagination: false,
      direction: "ltr",
      drag: true,
      snap: true,
      dragMinThreshold: {
        mouse: 0,
        touch: 50,
      },
    },
    i18n: {},
  };

  /**
   * @type {'pdf'|'slides'}
   */
  type;

  /**
   * @type {string}
   */
  #doublePageClass = "doublePages";

  /**
   * @type {Object}
   */
  controlElements = {};

  /**
   * @type {Object}
   */
  controlGroups = {};

  /**
   * @type {HTMLElement}
   */
  #splideTrack;

  /**
   * @type {HTMLUListElement}
   */
  #pagesListElement;

  /**
   * @type {Boolean}
   */
  #verbose;

  #eventCleanups = [];

  #animationFrames = new Set();

  #destroyed = false;

  #expandedLayoutReady = false;

  #expandedHeightRefreshPending = false;

  #expandedHeightSettleFramesRemaining = 0;

  #expandPageIndex;

  #expandRepositionWaitForFrame = false;

  #lastExpandViewportSize;

  /**
   * @type {HTMLDivElement}
   */
  splideRoot;

  /**
   * @type {HTMLElement}
   */
  bookContainer;

  /**
   * @type {HTMLElement}
   */
  bookLayout;

  /**
   * @type {HTMLElement}
   */
  rheaBook;

  /**
   * @type {Object}
   */
  options;

  /**
   * @type {BookInfo}
   */
  info;

  /**
   * @type {string}
   */
  index;

  /**
   * @type {PanZoomController}
   */
  zoomController;

  /**
   * @type {SidebarGestureController}
   */
  sidebarGestureController;

  sidebarPanels;

  downloadDialog;

  controlsComponent;

  pageAssets;

  /**
   * @type {Object}
   */
  thumbnails;

  /**
   * @type {HTMLLIElement[]}
   */
  pagesArray;

  /**
   * @type {HTMLElement}
   */
  #tocContainer;

  /**
   * @type {HTMLElement}
   */
  tocElement;

  /**
   * @type {Splide}
   */
  splide;

  /**
   * @type {HTMLElement}
   */
  controls;

  /**
   * @type {Number}
   */
  pageAspectWidth;

  /**
   * @type {Number}
   */
  pageAspectHeight;

  hasToc = false;

  /**
   * @param {HTMLElement} bookElement
   * @param {BookInfo} bookInfo
   * @param {object} options
   * @param {boolean} verbose
   * @param {object} controllers
   */
  constructor(
    bookElement,
    bookInfo,
    options,
    verbose = false,
    controllers = {},
  ) {
    const {
      PanZoomControllerClass = PanZoomController,
      ExpandHandlerClass = ExpandHandler,
      SplideHandlerClass = SplideHandler,
      SidebarGestureControllerClass = SidebarGestureController,
    } = controllers;

    this.#verbose = verbose || VERBOSE;
    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: new RheaBookViewer", this);

    this.splideRoot = bookElement;
    this.splideRoot.classList.add("rhea-root");
    const serverShell = this.#findServerShell();
    if (serverShell) {
      this.bookContainer = serverShell.bookContainer;
      this.bookLayout = serverShell.bookLayout;
      this.bookWrapper = serverShell.bookWrapper;
      this.rheaBook = serverShell.rheaBook;
    } else {
      this.bookContainer = document.createElement("div");
      this.bookContainer.classList.add("rhea-book-container");
      this.bookLayout = document.createElement("div");
      this.bookLayout.classList.add("rhea-book-layout");
      this.bookWrapper = document.createElement("div");
      this.bookWrapper.classList.add("rhea-book-wrapper");
      this.rheaBook = document.createElement("div");
      this.rheaBook.classList.add("rhea-book", "rhea-book-full");
      this.splideRoot.before(this.bookContainer);
      this.bookContainer.append(this.bookLayout);
      this.bookLayout.append(this.bookWrapper);
      this.bookWrapper.append(this.rheaBook);
      this.rheaBook.append(this.splideRoot);
    }

    this.options = mergeViewerOptions(RheaBookViewer.defaults, options);

    this.info = bookInfo;
    this.index =
      bookInfo.index ?? bookElement.dataset.bookIndex ?? bookElement.id;
    if (this.index === undefined || this.index === "") {
      throw new TypeError(
        "RheaBookViewer requires a book index or element id.",
      );
    }
    if (!bookElement.id) {
      const index = String(this.index);
      bookElement.id = /^\d+$/.test(index) ? `b-${index}` : index;
    }
    this.pageAssets = new PageAssets({
      root: this.splideRoot,
      info: this.info,
      i18n: this.options.i18n,
    });
    this.zoomController = new PanZoomControllerClass({
      container: this.bookContainer,
      element: this.rheaBook,
      pointerElement: this.bookWrapper,
      pinchStep: this.options.touchZoom,
      wheelZoom: this.options.zoom,
      maxScale: this.options.maxZoom,
      verbose: this.#verbose,
    });
    this.expandController = new ExpandHandlerClass({
      verbose: this.#verbose,
      container: this.bookContainer,
      fullscreen: this.options.fullscreen,
      removeScrolling: this.options.removeScrolling,
      classPrefix: "rhea",
    });
    this.splideController = new SplideHandlerClass(
      {
        verbose: this.#verbose,
        container: this.bookContainer,
        book: this.rheaBook,
        root: this.splideRoot,
        classPrefix: "rhea",
        splideOptions: {
          ...this.options.splideOptions,
          i18n: this.options.i18n,
        },
      },
      this,
    );
    this.sidebarGestureController = new SidebarGestureControllerClass({
      onSwipeLeft: () => {
        this.toggleThumbnails(false);
        this.toggleToc(false);
      },
    });

    this.bookContainer.id = this.splideRoot.id + "-container";

    this.#hookEvents();
    this.#build();

    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: created RheaBookViewer", this);
  }

  #hookEvents() {
    this.#listen(
      this.bookContainer,
      SplideHandler.pageChangedEvent,
      this.#handlePageChanged,
    );
    this.#listen(
      this.bookContainer,
      SplideHandler.navigationStateChangedEvent,
      this.#syncNavigationControls,
    );

    this.#listen(this.bookContainer, "click", (e) => {
      if (e.target.classList.contains("rhea-page-navigation--prev")) {
        this.goToPrevious();
      }

      if (e.target.classList.contains("rhea-page-navigation--next")) {
        this.goToNext();
      }
    });

    this.#listen(this.bookContainer, PanZoomController.zoomStartedEvent, () => {
      this.splideController.setDragEnabled(false);
      this.#upgradeVisiblePageAssets();
      this.#activateZoomOut();
    });
    this.#listen(this.bookContainer, PanZoomController.zoomEndedEvent, () => {
      if (!this.zoomController.isPinching()) {
        this.splideController.setDragEnabled(true);
      }
      this.#deactivateZoomOut();
      this.#activateZoomIn();
    });

    this.#listen(
      this.bookContainer,
      PanZoomController.pinchStartedEvent,
      () => {
        this.splideController.startPinch();
      },
    );
    this.#listen(this.bookContainer, PanZoomController.pinchEndedEvent, () => {
      this.splideController.endPinch(!this.zoomController.isZoomed());
    });

    this.#listen(
      this.bookContainer,
      PanZoomController.zoomChangedEvent,
      (event) => {
        if (event.detail.atMaximum) this.#deactivateZoomIn();
        else this.#activateZoomIn();
      },
    );

    this.#listen(window, "resize", this.#handleResize);
    if (window.visualViewport) {
      this.#listen(window.visualViewport, "resize", this.#handleViewportResize);
    }
    this.#listen(
      this.bookContainer,
      ExpandHandler.openEvent,
      this.#handleExpandTransitionStart,
    );
    this.#listen(
      this.bookContainer,
      ExpandHandler.closeEvent,
      this.#handleExpandTransitionStart,
    );
    this.#listen(
      this.bookContainer,
      ExpandHandler.openedEvent,
      this.#handleExpandOpened,
    );
    this.#listen(
      this.bookContainer,
      ExpandHandler.closedEvent,
      this.#handleExpandClosed,
    );
  }

  #build() {
    this.#buildBook();
    this.downloadDialog = new DownloadDialog({
      anchor: this.bookContainer,
      track: this.#splideTrack,
      info: this.info,
      i18n: this.options.i18n,
    });
    this.sidebarPanels = new SidebarPanels({
      bookWrapper: this.bookWrapper,
      idPrefix: this.splideRoot.id,
      bookIndex: this.index,
      thumbnailPages:
        this.options.thumbnails === true ? this.#buildPages(true) : undefined,
      toc: this.options.toc === true ? this.info.toc : undefined,
      gestureController: this.sidebarGestureController,
    });
    this.thumbnails = this.sidebarPanels.thumbnails;
    this.#tocContainer = this.sidebarPanels.tocContainer;
    this.tocElement = this.sidebarPanels.tocElement;
    this.hasToc = this.sidebarPanels.hasToc;
    this.#buildBookControls();

    this.#initBook();

    if (this.#checkSinglePage()) {
      this.#onlySinglePage();
    }
  }

  /**************************
   * EXTERNAL METHODS
   * Controls
   **************************/

  toggleToc(force) {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: toggleToc");
    return this.sidebarPanels?.toggleToc(force) ?? false;
  }

  toggleThumbnails(force) {
    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: toggleThumbnails");
    return this.sidebarPanels?.toggleThumbnails(force) ?? false;
  }

  goToFirst = () => {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: goToFirst");

    this.splideController.goToFirst();
  };

  goToPrevious = () => {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: goToPrevious");

    this.splideController.goToPrevious();
  };

  goToNext = () => {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: goToNext");

    this.splideController.goToNext();
  };

  goToLast = () => {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: goToLast");

    this.splideController.goToLast();
  };

  zoomIn() {
    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: zoomIn", this.options.zoom);
    this.zoomController.zoomBy(this.options.zoom);
  }

  zoomOut() {
    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: zoomOut", 1 / this.options.zoom);
    this.zoomController.zoomBy(1 / this.options.zoom);
  }

  changePageDisplay() {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: changeDisplay");
    const useDualPage = !this.bookContainer.classList.contains(
      this.#doublePageClass,
    );

    this.#setPageDisplay(useDualPage);
    this.#requestFrame(() => this.zoomController.refreshBounds());
  }

  expand() {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: expand");
    this.expandController.open();
  }

  compress() {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: compress");
    this.expandController.close();
  }

  showDownloadDialog() {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: download pages");
    return this.downloadDialog.show(
      this.bookContainer.classList.contains(this.#doublePageClass),
    );
  }

  /**************************
   * INTERNAL METHODS
   * Building
   **************************/
  #findServerShell() {
    const rheaBook = this.splideRoot.parentElement;
    const bookWrapper = rheaBook?.parentElement;
    const bookLayout = bookWrapper?.parentElement;
    const bookContainer = bookLayout?.parentElement;

    if (
      !rheaBook?.classList.contains("rhea-book") ||
      !bookWrapper?.classList.contains("rhea-book-wrapper") ||
      !bookLayout?.classList.contains("rhea-book-layout") ||
      !bookContainer?.classList.contains("rhea-book-container") ||
      !bookContainer.hasAttribute("data-rhea-viewer-shell")
    ) {
      return undefined;
    }

    return { bookContainer, bookLayout, bookWrapper, rheaBook };
  }

  #buildBook() {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: buildBook");

    this.#splideTrack = document.createElement("div");

    this.#splideTrack.classList.add("splide__track");
    this.#splideTrack.id = this.#id("pages");
    this.splideRoot.append(this.#splideTrack);

    this.#pagesListElement = document.createElement("ul");
    this.#pagesListElement.classList.add("splide__list");
    this.#splideTrack.append(this.#pagesListElement);

    this.#pagesListElement.append(...this.#buildPages());

    let goNext = document.createElement("div");
    goNext.classList.add("rhea-page-navigation", "rhea-page-navigation--next");

    let goPrev = document.createElement("div");
    goPrev.classList.add("rhea-page-navigation", "rhea-page-navigation--prev");

    this.splideRoot.append(goPrev, goNext);
  }

  #buildPages(thumbnails = false) {
    if (this.#verbose || VERBOSE)
      console.log(
        "RheaBookViewer: buildPages",
        thumbnails ? "thumbnails" : "fullsize",
      );
    let pages = [];

    for (let index in this.info.pageArray) {
      let pageInfo = this.info.pageArray[index];

      let page = document.createElement("li");
      if (!thumbnails) page.classList.add("splide__slide");
      if (!thumbnails) page.dataset.splideHash = this.index + "-" + index;
      if (!thumbnails)
        page.dataset.downloadPage =
          "page_" + String(index).padStart(4, "0") + ".pdf";

      let pageContainer = document.createElement("div");
      pageContainer.classList.add("rhea-page__interior");
      pageContainer.dataset.hash = this.index + "-" + index;
      page.append(pageContainer);

      let pageImage = this.pageAssets.createImage(pageInfo, index, thumbnails);

      if (thumbnails) {
        let pageLink = document.createElement("a");
        pageLink.classList.add("rhea-page__link");
        pageLink.href = "#" + this.index + "-" + index;
        pageLink.append(pageImage);
        pageContainer.append(pageLink);
      } else {
        pageContainer.append(pageImage);
      }

      pageContainer.style.aspectRatio = pageImage.style.aspectRatio;

      pages.push(page);
    }

    if (!thumbnails) {
      this.pagesArray = pages;
    }
    return pages;
  }

  #initBook() {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: initBook");

    this.splideController.init();
    this.#upgradeVisiblePageAssets();
    this.#markInitialPageReady(this.pageAssets.initialPageImage);
  }

  #buildBookControls() {
    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: buildBookControls");

    this.controlsComponent = new Controls({
      bookContainer: this.bookContainer,
      bookLayout: this.bookLayout,
      bookWrapper: this.bookWrapper,
      splideRoot: this.splideRoot,
      i18n: this.options.i18n,
      hasToc: this.hasToc,
      thumbnails: this.options.thumbnails,
      tocTarget: this.#tocContainer,
      thumbnailsTarget: this.thumbnails?.book,
      actions: {
        trackId: this.#splideTrack.id,
        toggleToc: () => this.toggleToc(),
        toggleThumbnails: () => this.toggleThumbnails(),
        first: this.goToFirst,
        previous: this.goToPrevious,
        next: this.goToNext,
        last: this.goToLast,
        zoomIn: () => this.zoomIn(),
        zoomOut: () => this.zoomOut(),
        changeDisplay: () => this.changePageDisplay(),
        expand: () => this.expand(),
        compress: () => this.compress(),
        downloadPages: () => this.showDownloadDialog(),
      },
    });

    // Keep the established public element references while the controls own
    // their DOM and responsive behavior internally.
    this.controls = this.controlsComponent.root;
    this.controlElements = this.controlsComponent.elements;
    this.controlGroups = this.controlsComponent.groups;
    this.controlsList = this.controlsComponent.primaryList;
    this.controlsOverflow = this.controlsComponent.overflow;
    this.controlsOverflowPanel = this.controlsComponent.overflowPanel;
    this.controlsOverflowList = this.controlsComponent.overflowList;
    this.sidebarPanels.setButtons({
      toc: this.controlElements.toc,
      thumbnails: this.controlElements.thumbnails,
    });

    this.#listen(this.bookWrapper, "wheel", (e) => {
      if (this.#verbose || VERBOSE)
        console.log("RheaBookViewer: wheelZoom", e.deltaY);
      if (this.rheaBook.contains(e.target)) {
        e.preventDefault();

        if (e.deltaY < 0 || this.zoomController.isZoomed()) {
          this.zoomController.zoomWithWheel(e);
        }
      }
    });
  }

  /**************************
   * INTERNAL METHODS
   * Checks
   **************************/

  #checkSinglePage() {
    return window.innerWidth < this.options.breakpoint;
  }

  /**************************
   * INTERNAL METHODS
   * Changers
   **************************/

  #onlySinglePage() {
    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: onlySinglePage", this);
    this.#setPageDisplayControlVisible(false);
    this.#setPageDisplay(false);
  }

  #setPageDisplayControlVisible(visible) {
    this.controlsComponent.setPageDisplayVisible(visible);
  }

  #setPageDisplay(useDualPage) {
    // SplideHandler calculates the book aspect ratio from this class, so
    // update it before changing Splide's per-page mode.
    this.bookContainer.classList.toggle(this.#doublePageClass, useDualPage);

    if (useDualPage) this.splideController.toDualPage();
    else this.splideController.toSinglePage();

    // A simultaneous fullscreen resize may already have retained a raw
    // Splide index. Display-mode changes add or remove a leading boundary
    // slide, so continue the fullscreen correction with the adjusted one.
    if (this.#expandPageIndex !== undefined) {
      this.#expandPageIndex = this.splideController.getNavigationState().index;
    }

    this.controlsComponent.setPageDisplayMode(useDualPage);
  }

  toggleControlsMenu(force) {
    return this.controlsComponent.toggleMenu(force);
  }

  #handleResize = () => {
    this.controlsComponent.syncLayout();
    if (
      this.#expandedLayoutReady ||
      this.#expandedHeightSettleFramesRemaining > 0
    ) {
      this.#queueExpandReposition();
      this.#scheduleExpandedHeightRefresh();
    }
    if (!this.controlElements.changeDisplay) return;

    const onlySinglePage = this.#checkSinglePage();
    this.#setPageDisplayControlVisible(!onlySinglePage);
    if (
      onlySinglePage &&
      this.bookContainer.classList.contains(this.#doublePageClass)
    ) {
      this.#onlySinglePage();
    }
  };

  #handleViewportResize = () => {
    if (
      this.#expandedLayoutReady ||
      this.#expandedHeightSettleFramesRemaining > 0
    ) {
      this.#queueExpandReposition();
      this.#scheduleExpandedHeightRefresh();
    }
  };

  #handleExpandTransitionStart = () => {
    this.#expandPageIndex = this.splideController.getNavigationState().index;
    this.#expandRepositionWaitForFrame = true;
  };

  #handleExpandOpened = () => {
    this.#expandedLayoutReady = true;
    // Fullscreen viewport metrics can keep changing after the opened event,
    // sometimes without another resize event. Re-measure for roughly the
    // first second of the transition so the final value is not missed.
    this.#expandedHeightSettleFramesRemaining = EXPANDED_HEIGHT_SETTLE_FRAMES;
    this.#scheduleExpandedHeightRefresh();
  };

  #handleExpandClosed = () => {
    this.#expandedLayoutReady = false;
    // Leaving native fullscreen is also asynchronous in Chrome. Keep
    // watching the viewport so a late Splide resize cannot leave its list
    // translated using the still-zoomed geometry.
    this.#expandedHeightSettleFramesRemaining = EXPANDED_HEIGHT_SETTLE_FRAMES;
    this.#scheduleExpandedHeightRefresh();
  };

  #queueExpandReposition = () => {
    this.#expandPageIndex ??= this.splideController.getNavigationState().index;
    this.#expandRepositionWaitForFrame = true;
  };

  #scheduleExpandedHeightRefresh = () => {
    if (this.#expandedHeightRefreshPending) return;

    this.#expandedHeightRefreshPending = true;
    this.#requestFrame(() => {
      this.#expandedHeightRefreshPending = false;
      this.#refreshExpandedHeight();

      if (this.#expandedHeightSettleFramesRemaining > 0) {
        this.#expandedHeightSettleFramesRemaining--;
        this.#scheduleExpandedHeightRefresh();
      } else if (this.#expandPageIndex !== undefined) {
        this.#scheduleExpandedHeightRefresh();
      }
    });
  };

  #refreshExpandedHeight = () => {
    const property = "--rhea-book-container-height";
    const containerHeight = getAvailableViewportHeight(window);
    const viewportSize = `${window.innerWidth}x${containerHeight ?? ""}`;
    let layoutChanged = false;

    if (viewportSize !== this.#lastExpandViewportSize) {
      this.#lastExpandViewportSize = viewportSize;
      this.#queueExpandReposition();
    }

    if (this.#expandedLayoutReady) {
      if (containerHeight !== undefined) {
        const height = `${containerHeight}px`;
        layoutChanged =
          this.bookContainer.style.getPropertyValue(property) !== height;
        if (layoutChanged) {
          this.bookContainer.style.setProperty(property, height);
        }
      }
    } else {
      layoutChanged =
        this.bookContainer.style.getPropertyValue(property) !== "";
      if (layoutChanged) this.bookContainer.style.removeProperty(property);
    }

    if (layoutChanged) this.#queueExpandReposition();

    if (this.#expandPageIndex !== undefined) {
      if (this.#expandRepositionWaitForFrame) {
        // Splide throttles its own window resize work to an animation
        // frame. Correct the position in the following frame so our
        // result is the final write to the list transform.
        this.#expandRepositionWaitForFrame = false;
      } else {
        this.splideController.reposition(this.#expandPageIndex);
        this.#expandPageIndex = undefined;
      }
    }

    if (this.#verbose || VERBOSE) {
      console.log("RheaBookViewer: refreshExpandedHeight", {
        containerHeight,
        viewportHeights: {
          visualViewport: window.visualViewport?.height,
          innerHeight: window.innerHeight,
          outerHeight: window.outerHeight,
          clientHeight: document.documentElement.clientHeight,
        },
      });
    }
  };

  #syncNavigationControls = ({ detail: state }) => {
    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: syncNavigationControls", state);
    this.controlsComponent?.setNavigationState(state);
  };

  #handlePageChanged = ({ detail: state }) => {
    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: handlePageChanged");
    this.#upgradeVisiblePageAssets(state.index);
    if (!this.zoomController.isZoomed()) return;

    this.#requestFrame(() => this.zoomController.refreshBounds());
  };

  #activateZoomIn = () => {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: activateZoomIn");
    this.controlsComponent?.setZoomInEnabled(true);
  };
  #activateZoomOut = () => {
    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: activateZoomOut");
    this.controlsComponent?.setZoomOutEnabled(true);
  };
  #deactivateZoomIn = () => {
    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: deactivateZoomIn");
    this.controlsComponent?.setZoomInEnabled(false);
  };
  #deactivateZoomOut = () => {
    if (this.#verbose || VERBOSE)
      console.log("RheaBookViewer: deactivateZoomOut");
    this.controlsComponent?.setZoomOutEnabled(false);
  };

  /**************************
   * INTERNAL METHODS
   * Helpers
   **************************/

  #id(id) {
    return this.splideRoot.id + "-" + id;
  }

  #markInitialPageReady(image) {
    if (!image) return;

    const markReadyAfterPaint = () => {
      this.#requestFrame(() => {
        // Keep the server-rendered preview over the mounted carousel for one
        // complete paint. This prevents Splide's measurement pass from
        // exposing an intermediate layout.
        this.#requestFrame(() => {
          this.pageAssets.removeInitialPreview();
          this.bookContainer.classList.remove("rhea-viewer-pending");
          this.bookContainer.classList.add("rhea-viewer-ready");
        });
      });
    };

    const decodeAndMarkReady = () => {
      const decoded =
        typeof image.decode === "function"
          ? image.decode().catch(() => undefined)
          : Promise.resolve();

      void decoded.then(markReadyAfterPaint);
    };

    const imageReady = () => {
      if (this.#destroyed) return;
      decodeAndMarkReady();
    };

    if (image.complete && image.naturalWidth > 0) {
      imageReady();
      return;
    }

    this.#listen(image, "load", imageReady, { once: true });
  }

  #requestFrame(callback) {
    const frame = globalThis.requestAnimationFrame(() => {
      this.#animationFrames.delete(frame);
      if (!this.#destroyed) callback();
    });
    this.#animationFrames.add(frame);
    return frame;
  }

  #cancelAnimationFrames() {
    for (const frame of this.#animationFrames) {
      globalThis.cancelAnimationFrame?.(frame);
    }
    this.#animationFrames.clear();
  }

  #upgradeVisiblePageAssets(index) {
    const navigationIndex =
      index ?? this.splideController.getNavigationState().index;
    this.pageAssets.upgradeVisible(
      this.#pagesListElement,
      navigationIndex,
      this.bookContainer.classList.contains(this.#doublePageClass),
    );
  }

  #listen(target, eventName, listener, options) {
    target.addEventListener(eventName, listener, options);
    this.#eventCleanups.push(() =>
      target.removeEventListener(eventName, listener, options),
    );
  }

  destroy() {
    if (this.#verbose || VERBOSE) console.log("RheaBookViewer: destroy");

    this.#destroyed = true;
    this.#cancelAnimationFrames();
    for (const cleanup of this.#eventCleanups.splice(0).reverse()) cleanup();
    this.pageAssets.destroy();
    this.downloadDialog?.destroy();
    this.controlsComponent?.destroy();
    this.sidebarPanels?.destroy();
    this.sidebarGestureController.destroy();
    this.expandController.destroy();
    this.#expandedLayoutReady = false;
    this.#expandedHeightSettleFramesRemaining = 0;
    this.#refreshExpandedHeight();
    this.zoomController.destroy();
    this.splideController.destroy();
    if (this.splideRoot.rhea === this) delete this.splideRoot.rhea;
  }
}
